/**
 * Permission levers (D57; plan: docs/briefs/roles-granular.md).
 *
 * A lever is one assigned / not-assigned power. Levers sit in groups; a group's master switch
 * is a UI convenience over the effective lever set, never stored and never checked by the
 * server. This file is pure data and pure functions: no I/O, no other workspace packages.
 *
 * Each row also says which scopes the lever's checks apply (`meta`): client records (`c`: a role limited to its
 * own clients reaches only those), branch records (`b`: a role limited to some branches reaches only those),
 * and which audit rows an audited lever writes. The old feature/action grants and sensitive switches that these
 * were derived from are gone (slice 9); nothing derives a lever set from them any more.
 */

/** The names under which an audited lever writes `sensitive.<name>` audit rows. Fixed: they are history in the audit log. */
export const AUDIT_NAMES = ["see_phone", "see_revenue", "refund", "edit_past_payment", "export_data"] as const;
export type AuditName = (typeof AUDIT_NAMES)[number];

/** Which scopes a lever's checks apply to: client records, branch records, both or neither. */
export type LeverScopeCode = "" | "c" | "b" | "cb";

interface LeverMeta {
  scope: LeverScopeCode;
  audit: readonly AuditName[];
  /** Audited without a named switch: an owner-approval lever. */
  ownerApproval: boolean;
}

const meta = (scope: LeverScopeCode, audit: readonly AuditName[] = [], ownerApproval = false): LeverMeta => ({ scope, audit, ownerApproval });

/**
 * Registry version. Bump when a lever is added; a post-release lever's `addedIn` is the new version.
 * v2 (D57 split): kiosks, bridges, expense categories, payees and recurring were split so each
 * lever matches exactly one old route gate. Nothing had been stored against v1, and the migration
 * derives these levers from legacy grants, so they keep `addedIn: 1` (they must not ship "off").
 * v3a (D57 split, slice 2b): `revenue.refund` stood for two gates on main: executing a refund
 * (payments:edit AND refund) and settling a credit note or a plan cancellation as a refund (the
 * refund switch alone). `revenue.refund_settlement` is the second; same `addedIn: 1` rule.
 * v3b (staff documents): staff.documents_view, staff.documents_edit, staff.bank_view_full and
 * staff.documents_download, `addedIn: 3`. Lever storage is on `main` now, so a role already stored at
 * v2 does not hold them and the Roles screen marks them New (D47); a role still on legacy grants
 * derives the first two from staff:view / staff:edit. The owner-only two derive off for every custom role.
 * v4: `billing.credit_note_over_cap` (founder ruling on #61, 2026-11-03, D59), owner approval for a
 * credit note above the role's discount cap. Owner-only legacy: no old grant derives it, so it ships
 * off for every custom role and only the owner holds it until the owner grants it.
 * v4 also: security review 2026-11-03, issues #63 and #64: five new levers that ship off (`addedIn: 4`).
 * Your own attendance and schedule are separate from everyone else's (`staff_attendance.correct_own`,
 * `staff.schedule_own`), and pay and money amounts in the audit log are separate from reading the log
 * (`roles.audit_payroll_amounts`, `roles.audit_commission_amounts`, `roles.audit_expense_amounts`). Acting on your own
 * pay (#62) is not a lever at all: nobody but the owner may.
 * v4 also: member-api adds `settings.member_app_view` and `settings.member_app_edit` (`addedIn: 4`, legacy NEVER, ship off): the registry
 * version was not bumped, as the Wave 6 rule says `addedIn` is the version current at kick-off.
 * v4 also: public-api adds `settings.public_pages_view` and `settings.public_pages_edit` on the same rule.
 */
export const LEVER_REGISTRY_VERSION = 4;

export const GROUPS = [
  { id: "members", label: "Members" },
  { id: "leads", label: "Leads" },
  { id: "billing", label: "Billing" },
  { id: "revenue", label: "Revenue" },
  { id: "reports", label: "Reports" },
  { id: "pt", label: "Personal training" },
  { id: "classes", label: "Classes" },
  { id: "staff", label: "Staff" },
  { id: "staff_attendance", label: "Staff attendance" },
  { id: "payroll", label: "Payroll" },
  { id: "commission", label: "Commission" },
  { id: "expenses", label: "Expenses" },
  { id: "catalogue", label: "Catalogue" },
  { id: "inbox", label: "Inbox" },
  { id: "devices", label: "Devices" },
  { id: "settings", label: "Settings" },
  { id: "exports", label: "Exports" },
  { id: "roles", label: "Roles and audit" },
] as const;
export type GroupId = (typeof GROUPS)[number]["id"];

type Row = readonly [id: string, label: string, hint: string, meta: LeverMeta, requires?: readonly string[], addedIn?: number];

const SETTINGS_SECTIONS = [
  ["business", "Business"],
  ["brands_branches", "Brands and branches"],
  ["invoicing", "Invoicing"],
  ["payment_accounts", "Payment accounts"],
  ["memberships", "Membership rules"],
  ["leads_setup", "Lead stages and intake keys"],
  ["attendance", "Attendance rules"],
  ["biometric", "Biometric rules"],
  ["commission", "Commission settings"],
  ["payroll", "Payroll settings"],
  ["messaging", "Messaging connections and templates"],
  ["imports", "Imports and onboarding"],
] as const;

const settingsRows = SETTINGS_SECTIONS.flatMap(([key, label]): Row[] => [
  [`settings.${key}_view`, `See ${label.toLowerCase()}`, `Open the ${label.toLowerCase()} settings.`, meta("")],
  [
    `settings.${key}_edit`,
    `Change ${label.toLowerCase()}`,
    `Save changes to the ${label.toLowerCase()} settings.`,
    meta(""),
    // Compound: mapping imported roles (PUT /imports/:id/roles) also needs roles:view on main.
    key === "imports" ? [`settings.${key}_view`, "roles.roles_view"] : [`settings.${key}_view`],
  ],
]);

const ROWS = [
  // Members (15)
  ["members.view", "See members", "Member list and profiles.", meta("cb")],
  ["members.add", "Add members", "Create a member record.", meta("cb")],
  ["members.edit_profile", "Edit member profiles", "Change a member's details and date of birth.", meta("cb")],
  ["members.see_phone", "See member phone numbers", "Unmasked phone numbers on member records.", meta("", ["see_phone"])],
  ["members.freeze", "Freeze and unfreeze plans", "Includes unfreeze and bulk freeze.", meta("cb"), ["billing.create_bill"]],
  ["members.extend", "Extend plans", "Add days to a membership.", meta("cb")],
  ["members.transfer", "Transfer a plan", "Move a plan to another member.", meta("cb"), ["billing.create_bill"]],
  ["members.cancel_plan", "Cancel a plan", "End a membership early.", meta("cb"), ["revenue.refund_settlement"]],
  ["members.override_start", "Override a plan start date", "Start a plan on a date other than the default.", meta("cb")],
  ["members.biometric_consent", "Record member biometric consent", "Record or withdraw a member's consent.", meta("cb")],
  ["members.checkins", "See check-in history", "Check-in log, per member and for the whole gym.", meta("cb")],
  ["members.at_risk", "See at-risk members", "Members who have stopped coming.", meta("cb")],
  ["members.opt_out", "Manage message opt-outs", "Opt a member out of or back into messages.", meta("cb")],
  ["members.message_log", "See the message log", "Messages sent to members and the opt-out list.", meta("cb")],
  ["members.frontdesk_code", "Set a member's front-desk code", "Issue or reset a member's front-desk code.", meta("cb")],

  // Leads (7)
  ["leads.view", "See leads", "Lead board, list and profiles.", meta("cb")],
  ["leads.add", "Add leads", "Create a lead.", meta("cb")],
  ["leads.edit", "Edit leads", "Change a lead's details and tasks.", meta("cb")],
  ["leads.move", "Move leads between stages", "Change a lead's stage.", meta("cb")],
  ["leads.convert", "Convert leads to members", "Turn a lead into a member.", meta("cb")],
  ["leads.delete", "Delete leads", "Remove a lead.", meta("cb")],
  ["leads.see_phone", "See lead phone numbers", "Unmasked phone numbers on leads.", meta("", ["see_phone"])],

  // Billing (11)
  ["billing.bills_view", "See bills and invoices", "Bills, invoices and credit notes.", meta("cb")],
  ["billing.create_bill", "Create bills", "Sell a plan or item.", meta("cb")],
  ["billing.renew", "Renew plans", "Renew a membership with a new bill.", meta("cb")],
  ["billing.upgrade", "Upgrade plans", "Move a member to a higher plan.", meta("cb")],
  ["billing.discount", "Give discounts", "Discount a bill, up to the role's cap.", meta("cb"), ["billing.create_bill"]],
  ["billing.credit_note", "Issue credit notes", "Credit part or all of a bill.", meta("cb")],
  [
    "billing.credit_note_over_cap",
    "Credit notes above the discount cap",
    "Credit a plan beyond the role's discount cap, or credit any line to the wallet beyond it. Owner approval.",
    meta("", [], true),
    ["billing.credit_note"],
  ],
  ["billing.collect_payment", "Collect payments", "Record a payment against a bill.", meta("cb"), ["billing.create_bill"]],
  ["billing.payments_view", "See payments", "Payment list and payment intents.", meta("cb")],
  ["billing.payment_intent_manage", "Manage payment links", "Cancel or re-check a payment intent.", meta("cb")],
  ["billing.dues", "See dues", "Who owes what, on Home and the dues page.", meta("cb", ["see_revenue"])],

  // Revenue (8)
  ["revenue.home_collections", "See collections on Home", "The collected-today figure.", meta("cb", ["see_revenue"])],
  ["revenue.bill_amounts", "See bill amounts", "Amounts on bills, payments, invoices and refunds.", meta("cb", ["see_revenue"])],
  ["revenue.member_balance", "See member balances", "A member's balance and wallet.", meta("", ["see_revenue"])],
  ["revenue.refunds_view", "See refunds", "The refunds list.", meta("cb")],
  ["revenue.refund", "Give refunds", "Execute a refund.", meta("cb", ["refund"])],
  ["revenue.refund_settlement", "Settle as a refund", "Pay a credit note or a cancelled plan back as a refund.", meta("", ["refund"])],
  ["revenue.void_bill", "Void bills", "Void a bill after the fact.", meta("cb", ["edit_past_payment"])],
  ["revenue.resolve_payment_review", "Resolve payments under review", "Settle a payment held for review.", meta("cb", ["edit_past_payment"])],

  // Reports (8)
  ["reports.collections", "Collections report", "Money collected over a period.", meta("b", ["see_revenue"])],
  ["reports.dues", "Dues report", "Outstanding dues.", meta("b", ["see_revenue"])],
  ["reports.renewals_due", "Renewals-due report", "Plans about to expire.", meta("b")],
  ["reports.attendance", "Attendance report", "Member visits over a period.", meta("b")],
  ["reports.pt_delivery", "PT delivery report", "Personal-training sessions delivered.", meta("b")],
  ["reports.payroll", "Payroll report", "Payroll totals.", meta("b", ["see_revenue"])],
  ["reports.lead_funnel", "Lead funnel report", "Leads by stage and source.", meta("b")],
  ["reports.expenses", "Expenses report", "Spend by category.", meta("b", ["see_revenue"])],

  // PT (12)
  ["pt.view", "See PT sessions", "PT calendar and sessions.", meta("cb")],
  ["pt.book", "Book PT sessions", "Book a personal-training session.", meta("cb"), ["staff.see_trainer_names"]],
  ["pt.reschedule", "Reschedule PT sessions", "Move a session.", meta("cb")],
  ["pt.override", "Override PT scheduling rules", "Reschedule outside the normal rules.", meta("cb"), ["pt.reschedule"]],
  ["pt.cancel", "Cancel PT sessions", "Cancel a session.", meta("cb")],
  ["pt.complete", "Complete PT sessions", "Mark a session done or a no-show.", meta("cb")],
  ["pt.waive", "Waive PT sessions", "Waive a session without delivery.", meta("cb")],
  ["pt.change_trainer", "Change a session's trainer", "Hand a session to another trainer.", meta("cb"), ["staff.see_trainer_names"]],
  ["pt.availability", "Set trainer availability", "Edit a trainer's open hours.", meta("cb")],
  ["pt.pack_balance", "See PT pack balances", "Sessions left on a pack.", meta("cb")],
  ["pt.schedule_pattern", "Book and change PT patterns", "Book a pack on a fixed weekly slot, or move its future sessions.", meta("cb")],
  ["pt.auto_mark_settings", "Change PT auto-marking", "Turn marking from check-ins and end of day on or off, and set the booking style.", meta("")],

  // Classes (11)
  ["classes.view", "See classes", "Class schedule and bookings.", meta("b")],
  ["classes.template_add", "Add class templates", "Create a recurring class.", meta("b"), ["staff.see_trainer_names"]],
  ["classes.template_edit", "Edit class templates", "Change a recurring class and its exceptions.", meta("b"), ["staff.see_trainer_names"]],
  ["classes.template_delete", "Delete class templates", "Remove a recurring class.", meta("b")],
  ["classes.book", "Book members into classes", "Reserve a class place.", meta("b")],
  ["classes.cancel_occurrence", "Cancel a class occurrence", "Cancel one class.", meta("b")],
  ["classes.complete_occurrence", "Complete a class occurrence", "Mark one class as held.", meta("b")],
  ["classes.cancel_booking", "Cancel class bookings", "Cancel a member's booking.", meta("b")],
  ["classes.attendance", "Mark class attendance", "Record who attended.", meta("b")],
  ["classes.waive", "Waive class bookings", "Waive a late-cancel or no-show charge.", meta("b")],
  ["classes.refunds_due", "See class refunds due", "Bookings owed a refund.", meta("b")],

  // Staff (16)
  ["staff.view", "See staff", "Staff list and profiles.", meta("b")],
  ["staff.add", "Add staff", "Invite a staff member.", meta("b")],
  ["staff.edit_profile", "Edit staff profiles", "Change a staff member's details.", meta("b")],
  ["staff.schedule", "Edit staff schedules", "Set weekly working hours.", meta("b")],
  ["staff.schedule_own", "Edit own schedule", "Set your own weekly working hours, which change your absence deductions.", meta(""), ["staff.schedule"], 4],
  ["staff.deactivate", "Deactivate staff", "Remove a staff member's access.", meta("b")],
  ["staff.see_phone", "See staff phone numbers", "Unmasked phone numbers on staff records.", meta("", ["see_phone"])],
  ["staff.biometric_consent", "Record staff biometric consent", "Record or withdraw a staff member's consent.", meta("b")],
  ["staff.tasks_assign", "Assign tasks to others", "Give a task to another staff member.", meta("b")],
  ["staff.tasks_view_others", "See others' tasks", "Team view of tasks.", meta("b")],
  ["staff.see_names", "See colleagues' names", "Names on lead owners, task assignees and the audit log.", meta("b")],
  ["staff.see_trainer_names", "See trainer names", "Names on PT, class schedules, templates and pickers.", meta("b")],
  ["staff.documents_view", "See staff ID and bank details", "PAN, Aadhaar last 4, date of birth, address, emergency contact, masked bank account and the document list.", meta("b")],
  ["staff.documents_edit", "Edit staff ID and bank details", "Change those details, upload and delete documents.", meta("b"), ["staff.documents_view"]],
  ["staff.bank_view_full", "See full bank account numbers", "Reveal a staff member's full account number. Every reveal is audited.", meta("", [], true), ["staff.documents_view"]],
  ["staff.documents_download", "Download staff documents", "Open PAN cards, ID proofs, cheques and offer letters. Every download is audited.", meta("", [], true), ["staff.documents_view"]],

  // Staff attendance (4)
  ["staff_attendance.view", "See staff attendance", "Staff punches and days.", meta("b")],
  ["staff_attendance.correct", "Correct staff attendance", "Add punches and fix days.", meta("b")],
  ["staff_attendance.correct_own", "Correct own attendance", "Add punches to and fix days on your own record, which changes your pay.", meta(""), ["staff_attendance.correct"], 4],
  ["staff_attendance.holidays", "Manage the holiday calendar", "Add and remove holidays.", meta("")],

  // Payroll (7)
  ["payroll.view", "See payroll", "Payroll months, profiles and payslips.", meta("b")],
  ["payroll.profiles_edit", "Edit payroll profiles", "Pay terms for each person.", meta("b")],
  ["payroll.advances", "Record advances and adjustments", "Advances and one-off adjustments.", meta("b")],
  ["payroll.draft", "Draft a payroll month", "Build the month's payslips.", meta("b")],
  ["payroll.approve", "Approve payroll", "Approve a drafted month.", meta("b")],
  ["payroll.lock", "Lock payroll", "Lock an approved month.", meta("b")],
  ["payroll.mark_paid", "Mark payslips paid", "Record that a payslip was paid.", meta("b")],

  // Commission (6)
  ["commission.rules_view", "See commission rules", "Rule list and versions.", meta("cb")],
  ["commission.rules_edit", "Edit commission rules", "Create, version and retire rules.", meta("cb")],
  ["commission.preview", "Preview commission", "Dry-run commission before posting.", meta("cb")],
  ["commission.post", "Post commission", "Post commission to the ledger.", meta("cb")],
  ["commission.ledger_view", "See the commission ledger", "Ledger entries.", meta("cb")],
  ["commission.release", "Release held commission", "Release commission that was held.", meta("cb")],

  // Expenses (15)
  ["expenses.view", "See expenses", "Expense list and details.", meta("b")],
  ["expenses.add", "Add expenses", "Record an expense.", meta("b")],
  ["expenses.edit", "Edit expenses", "Change an expense.", meta("b")],
  ["expenses.delete", "Delete expenses", "Remove an expense.", meta("b")],
  ["expenses.categories_add_edit", "Add and rename expense categories", "Create a category or change its name.", meta("b")],
  ["expenses.categories_delete", "Delete expense categories", "Remove a category.", meta("b")],
  ["expenses.payees_add_edit", "Add and change payees", "Create a payee or change their details.", meta("b")],
  ["expenses.payees_delete", "Delete payees", "Remove a payee.", meta("b")],
  ["expenses.recurring_add", "Add recurring expenses", "Create a repeating expense.", meta("b")],
  ["expenses.recurring_edit", "Change recurring expenses", "Edit a repeating expense.", meta("b")],
  ["expenses.recurring_delete", "Delete recurring expenses", "Remove a repeating expense.", meta("b")],
  ["expenses.recurring_post", "Post recurring expenses", "Post a repeating expense now.", meta("b")],
  ["expenses.attachments", "Manage expense attachments", "Add and remove receipts.", meta("b")],
  ["expenses.payee_phone", "See payee phone numbers", "Unmasked phone numbers on payees.", meta("", ["see_phone"])],
  ["expenses.import", "Import expenses from a bank statement", "Pick transactions from a statement file and add them as expenses.", meta("b"), ["expenses.add"]],

  // Catalogue (8)
  ["catalogue.plans_view", "See plans and items", "The catalogue.", meta("b")],
  ["catalogue.plans_add", "Add plans and items", "Create a catalogue item.", meta("b")],
  ["catalogue.plans_edit", "Edit plans and items", "Change a catalogue item.", meta("b")],
  ["catalogue.plans_delete", "Delete plans and items", "Remove a catalogue item.", meta("b")],
  ["catalogue.coupons_view", "See coupons", "Coupon list.", meta("b")],
  ["catalogue.coupons_add", "Add coupons", "Create a coupon.", meta("b")],
  ["catalogue.coupons_edit", "Edit coupons", "Change a coupon.", meta("b")],
  ["catalogue.coupons_delete", "Delete coupons", "Remove a coupon.", meta("b")],

  // Inbox (3)
  ["inbox.view", "See the inbox", "Conversations and messages.", meta("b")],
  ["inbox.reply", "Reply in the inbox", "Send text or template messages.", meta("b")],
  ["inbox.manage", "Assign and archive conversations", "Assignment, archive and read state.", meta("b")],

  // Devices (10)
  ["devices.view", "See devices", "Kiosks, bridges and terminals.", meta("b")],
  ["devices.kiosks_add", "Add kiosks", "Create a check-in kiosk.", meta("b")],
  ["devices.kiosks_edit", "Change kiosks", "Edit a check-in kiosk.", meta("b")],
  ["devices.kiosk_revoke", "Revoke kiosks", "Remove a kiosk's access.", meta("b")],
  ["devices.bridges_add", "Add bridges", "Add a device bridge.", meta("b")],
  ["devices.bridges_remove", "Remove bridges", "Remove a device bridge.", meta("b")],
  ["devices.terminals_map", "Map terminals", "Rename terminals and map their users.", meta("b")],
  ["devices.biometric_enrol", "Enrol and copy biometrics", "Enrol a member or staff member, or copy to another terminal.", meta("b")],
  ["devices.biometric_wipe", "Wipe biometrics", "Remove a person's biometric from terminals.", meta("b")],
  ["devices.checkin_keys", "Rotate check-in keys", "Rotate the signed-code keys.", meta("b")],

  // Settings (24)
  ...settingsRows,
  // Member app (slice member-api, docs/briefs/member-app.md). New levers, so no old grant derives them (NEVER): they ship
  // off for every custom role and only the owner holds them (D47). `addedIn` is the registry version current at kick-off.
  ["settings.member_app_view", "See member app settings", "Open the member app settings: booking windows, cancel rules and which tabs members see.", meta(""), undefined, 4],
  ["settings.member_app_edit", "Change member app settings", "Save changes to the member app settings.", meta(""), ["settings.member_app_view"], 4],
  // Helmr support (slice console-support). New lever, so no old grant derives it (NEVER): it ships off for every custom role
  // and only the owner holds it (D47). `addedIn` is the registry version current at kick-off. See only: a gym cannot block support.
  ["settings.support_access_view", "See Helmr support sessions", "Open the list of times Helmr support looked inside this business, who, why and for how long.", meta(""), undefined, 4],
  // Public pages (slice public-api, docs/briefs/public-pages.md). Same rule: off for every custom role, owner holds them.
  ["settings.public_pages_view", "See public pages settings", "Open the public pages settings: whether the pages are on, which plans and prices show, and the contact details.", meta(""), undefined, 4],
  ["settings.public_pages_edit", "Change public pages settings", "Turn the gym's public pages on or off and change what they show.", meta(""), ["settings.public_pages_view"], 4],
  // Go-live checklist (slice pilot-golive, docs/briefs/pilot-onboarding.md). Same rule: off for every custom role, owner holds them.
  ["settings.golive_view", "See the go-live checklist", "Open the checklist of what is left before the gym goes live: data, WhatsApp, devices and staff.", meta(""), undefined, 4],
  ["settings.golive_edit", "Tick go-live items", "Tick a go-live item by hand, with a reason, when no automatic check can see it.", meta(""), ["settings.golive_view"], 4],

  // Exports (15)
  ["exports.leads", "Export leads", "Download leads as CSV.", meta("cb", ["export_data"])],
  ["exports.bills", "Export bills", "Download bills as CSV.", meta("cb", ["export_data"])],
  ["exports.invoices", "Export invoices", "Download invoices as CSV.", meta("cb", ["export_data"])],
  ["exports.staff_attendance", "Export staff attendance", "Download staff attendance as CSV.", meta("b", ["export_data"])],
  ["exports.commission_ledger", "Export the commission ledger", "Download the ledger as CSV.", meta("cb", ["export_data"])],
  ["exports.payroll", "Export payroll", "Download payroll as CSV.", meta("b", ["export_data"])],
  ["exports.expenses", "Export expenses", "Download expenses as CSV.", meta("b", ["export_data"])],
  ["exports.report_collections", "Export the collections report", "Download as CSV.", meta("b", ["export_data", "see_revenue"])],
  ["exports.report_dues", "Export the dues report", "Download as CSV.", meta("b", ["export_data", "see_revenue"])],
  ["exports.report_renewals_due", "Export the renewals-due report", "Download as CSV.", meta("b", ["export_data"])],
  ["exports.report_attendance", "Export the attendance report", "Download as CSV.", meta("b", ["export_data"])],
  ["exports.report_pt_delivery", "Export the PT delivery report", "Download as CSV.", meta("b", ["export_data"])],
  ["exports.report_payroll", "Export the payroll report", "Download as CSV.", meta("b", ["export_data", "see_revenue"])],
  ["exports.report_lead_funnel", "Export the lead funnel report", "Download as CSV.", meta("b", ["export_data"])],
  ["exports.report_expenses", "Export the expenses report", "Download as CSV.", meta("b", ["export_data", "see_revenue"])],

  // Roles and audit (9)
  ["roles.roles_view", "See roles", "Role list and details.", meta("")],
  ["roles.roles_add", "Create roles", "Add a custom role.", meta("")],
  ["roles.roles_edit", "Edit roles", "Change a role's levers and scope.", meta("")],
  ["roles.roles_delete", "Delete roles", "Remove a custom role.", meta("")],
  ["roles.roles_assign", "Assign roles to staff", "Change which role a person holds.", meta("b")],
  ["roles.audit_view", "See the audit log", "Who did what, and when.", meta("b")],
  ["roles.audit_payroll_amounts", "See pay in the audit log", "Salaries, TDS, advances, adjustments and payslip amounts on payroll entries.", meta(""), ["roles.audit_view"], 4],
  ["roles.audit_commission_amounts", "See commission in the audit log", "Rule rates and posted amounts on commission entries.", meta(""), ["roles.audit_view"], 4],
  ["roles.audit_expense_amounts", "See expenses in the audit log", "Amounts and details on expense entries.", meta(""), ["roles.audit_view"], 4],
] as const satisfies readonly Row[];

export type LeverId = (typeof ROWS)[number][0];

export interface LeverDef {
  id: LeverId;
  group: GroupId;
  label: string;
  /** One line shown under the switch. */
  hint: string;
  /** Registry version that introduced it. */
  addedIn: number;
  /** Shown in the UI as "Needs: ..."; never auto-toggled. */
  requires?: LeverId[];
  /** Every check writes an audit row: true where the old check was a sensitive switch. */
  audited?: boolean;
  /** Which scopes the lever's checks apply: client records, branch records, both or neither. */
  scoped: { clients: boolean; branches: boolean };
  /** The `sensitive.<name>` audit rows an audited lever's check writes, one per name, in this order. */
  auditNames: readonly AuditName[];
}

/** Levers added after the first stored registry: their `addedIn`. */
const ADDED_IN: Readonly<Record<string, number>> = {
  // bank-statement import. Legacy = expenses:add, so every role that can add an expense can import.
  "expenses.import": 3,
  "staff.documents_view": 3,
  "staff.documents_edit": 3,
  "staff.bank_view_full": 3,
  "staff.documents_download": 3,
  // pt-schedules: each carries the legacy gate of the route it guards, so nobody gains or loses access.
  "pt.schedule_pattern": 3,
  "pt.auto_mark_settings": 3,
  // Founder ruling on #61 (D59): owner approval above the discount cap. Owner-only, ships off.
  "billing.credit_note_over_cap": 4,
};

export const LEVERS: readonly LeverDef[] = (ROWS as readonly Row[]).map(([id, label, hint, m, requires, rowAddedIn]) => {
  const def: LeverDef = {
    id: id as LeverId,
    group: id.slice(0, id.indexOf(".")) as GroupId,
    label,
    hint,
    addedIn: rowAddedIn ?? ADDED_IN[id] ?? 1,
    scoped: { clients: m.scope.includes("c"), branches: m.scope.includes("b") },
    auditNames: m.audit,
  };
  if (requires) def.requires = [...requires] as LeverId[];
  if (m.audit.length > 0 || m.ownerApproval) def.audited = true;
  return def;
});

export const LEVER_IDS: readonly LeverId[] = LEVERS.map((l) => l.id);

const LEVER_BY_ID: ReadonlyMap<string, LeverDef> = new Map(LEVERS.map((l) => [l.id, l]));

export function isLeverId(value: unknown): value is LeverId {
  return typeof value === "string" && LEVER_BY_ID.has(value);
}

export function getLever(id: LeverId): LeverDef {
  return LEVER_BY_ID.get(id) as LeverDef;
}

export function isGroupId(value: unknown): value is GroupId {
  return typeof value === "string" && GROUPS.some((g) => g.id === value);
}

export function leversInGroup(group: GroupId): LeverId[] {
  return LEVERS.filter((l) => l.group === group).map((l) => l.id);
}

export type GroupState = "on" | "off" | "partial";

/** A group is on when every lever in it is held, off when none is, partial otherwise. */
export function groupState(levers: Iterable<string>, group: GroupId): GroupState {
  const held = new Set(levers);
  const ids = leversInGroup(group);
  const n = ids.filter((id) => held.has(id)).length;
  if (n === 0) return "off";
  return n === ids.length ? "on" : "partial";
}

export interface LeverDiff {
  added: LeverId[];
  removed: LeverId[];
}

/** What changes going from set `a` to set `b`; both lists sorted. */
export function diffLevers(a: Iterable<LeverId>, b: Iterable<LeverId>): LeverDiff {
  const from = new Set(a);
  const to = new Set(b);
  return {
    added: [...to].filter((id) => !from.has(id)).sort(),
    removed: [...from].filter((id) => !to.has(id)).sort(),
  };
}
